import React, { useState, useEffect, useRef } from "react";
import { Upload, FileText, Box, Save } from "lucide-react";
import PropTypes from "prop-types";
import ReportModal from "./reportgenerator";
import BlastSelectionModal from "./blasselection";
import SaveModel from "./saveanalysisrecord";
import PotreeControls from "../components/potreecontrols";
import { useFile } from "../context/filecontext";

const PotreeViewer = ({
  viewerContainerRef,
  handleUploadNew,
  selectedBlasts,
  measurements,
  metadata,
  viewer,
  onBlastSelection,
  blastOptions,
  setOreFile,
}) => {
  const [isBlastModalOpen, setIsBlastModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  // create ref
  let { loaderRef, loaderText } = useFile();
  const [sessionBlasts, setSessionBlasts] = useState(() => {
    try {
      const saved = sessionStorage.getItem("selectedBlasts");
      return saved ? JSON.parse(saved) : selectedBlasts;
    } catch (error) {
      console.error("Error reading from sessionStorage:", error);
      return selectedBlasts;
    }
  });

  // Effect to save selections to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem("selectedBlasts", JSON.stringify(sessionBlasts));
      onBlastSelection(sessionBlasts);
    } catch (error) {
      console.error("Error saving to sessionStorage:", error);
    }
  }, [sessionBlasts, onBlastSelection]);

  const handleReportClick = () => {
    setIsReportModalOpen(true);
  };

  const handleSaveClick = () => {
    setIsSaveModalOpen(true);
  };

  const handleBlastSelectionChange = (newSelections) => {
    setSessionBlasts(newSelections);
  };

  // Initialize Potree viewer with additional settings
  useEffect(() => {
    if (viewer) {
      // Set initial viewer settings
      viewer.setPointBudget(3_500_000); // Increased point budget for better quality
      viewer.setFOV(40);
      viewer.setEDLEnabled(true);
      // viewer.setDescription('Point Cloud Viewer');

      // Add event listeners for viewer interactions
      viewer.addEventListener("point_budget_changed", (e) => {
        console.log("Point budget changed:", e.value);
      });

      viewer.addEventListener("background_changed", (e) => {
        console.log("Background changed:", e.value);
      });
    }
  }, [viewer]);

  const closeModal = () => {
    setIsBlastModalOpen(false);
    onBlastSelection([]);
    setSessionBlasts([]);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header Section */}
      <header className="bg-white border-b border-gray-200 shadow-md px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo / Title */}
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-wide">
            Stope Visualiser
          </h1>

          {/* Loader Animation */}
          <div ref={loaderRef} className="displayNone">
            <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg">
              <div className="w-5 h-5 border-4 border-dashed rounded-full animate-spin border-blue-600"></div>
              <span ref={loaderText} className="text-gray-600">
                Processing...
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            {/* Upload Button */}
            <button
              onClick={handleUploadNew}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              <Upload className="h-4 w-4 text-gray-500" />
              Upload
            </button>

            {/* Save Button (Primary) */}
            <button
              onClick={handleSaveClick}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition"
            >
              <Save className="h-4 w-4 text-white" />
              Save
            </button>

            {/* Select Blast Button with Badge */}
            <button
              onClick={() => setIsBlastModalOpen(true)}
              className="relative inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              <Box className="h-4 w-4 text-gray-500" />
              Select Blast
              {sessionBlasts.length > 0 && (
                <span className="absolute top-0 right-0 -mt-1 -mr-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                  {sessionBlasts.length}
                </span>
              )}
            </button>

            {/* Report Button (Highlighted) */}
            <button
              onClick={handleReportClick}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition"
            >
              <FileText className="h-4 w-4 text-white" />
              Report
            </button>
          </div>
        </div>
      </header>

      {/* Main Viewer Section */}
      <main className="flex-1 relative">
        <div
          ref={viewerContainerRef}
          id="potree_render_area"
          className="absolute inset-0 bg-gray-100"
        ></div>
        {viewer && <PotreeControls viewer={viewer} />}
      </main>

      {/* Modals */}
      <BlastSelectionModal
        isOpen={isBlastModalOpen}
        onClose={() => closeModal()}
        onBlastSelection={handleBlastSelectionChange}
        blastOptions={blastOptions || []}
        selectedBlasts={selectedBlasts}
        setOreFile={setOreFile}
        setModal={setIsBlastModalOpen}
      />

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        measurements={measurements || {}}
        viewer={viewer}
      />

      <SaveModel
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        measurements={measurements || {}}
        viewer={viewer}
      />
    </div>
  );
};

PotreeViewer.propTypes = {
  viewerContainerRef: PropTypes.object,
  handleUploadNew: PropTypes.func.isRequired,
  selectedBlasts: PropTypes.array.isRequired,
  measurements: PropTypes.object,
  metadata: PropTypes.object,
  viewer: PropTypes.object,
  onBlastSelection: PropTypes.func.isRequired,
  blastOptions: PropTypes.array,
};

export default PotreeViewer;
